import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, getToken } from "@/lib/api";
import {
  Buildings, House, ChartLineUp, FileText, ArrowRight, Download,
  CurrencyEur, CalendarDots, Briefcase, Warning, Receipt, FileArrowDown, Eye, Clock,
  TrendUp, TrendDown, FilePdf, Sparkle, ShieldCheck,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";

const NAVY = "#0F2A4D";
const NAVY_SOFT = "#1B3A66";
const NAVY_GLOW = "#1E4A86";

function eur(v) {
  return `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
function fmtDate(s) { return s ? new Date(s).toLocaleDateString("de-DE") : "—"; }
const MONTH_NAMES = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function formalGreeting(contact) {
  if (!contact) return "Willkommen zurück";
  const last = contact.last_name || contact.company_name || "";
  if (contact.salutation === "herr") return `Willkommen zurück, Herr ${last}`;
  if (contact.salutation === "frau") return `Willkommen zurück, Frau ${last}`;
  if (contact.company_name) return `Willkommen zurück, ${contact.company_name}`;
  return last ? `Willkommen zurück, ${last}` : "Willkommen zurück";
}

function isNewDoc(createdAt, days = 14) {
  if (!createdAt) return false;
  const diff = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= days;
}

function fileExt(d) {
  const n = d?.original_filename || d?.title || "";
  const m = n.match(/\.([a-z0-9]{2,5})$/i);
  return (m ? m[1] : (d?.content_type?.includes("pdf") ? "pdf" : "")).toUpperCase();
}

// ============================================================================
// OWNER HOME = "Meine Objekte" – Hero + KPIs + Property-Grid + Letzte Dokumente
// ============================================================================
export function OwnerHome() {
  const { tenant } = useAuth();
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [costs, setCosts] = useState(null);
  const [docs, setDocs] = useState([]);
  const [previewDocId, setPreviewDocId] = useState(null);

  useEffect(() => {
    api.get("/owner-portal/me").then((r) => setMe(r.data));
    api.get("/owner-portal/overview").then((r) => setOverview(r.data));
    api.get("/owner-portal/costs").then((r) => setCosts(r.data));
    api.get("/owner-portal/documents").then((r) => setDocs(r.data || []));
  }, []);

  if (!overview || !costs) return <SkeletonDashboard />;

  const brand = tenant?.display_name || tenant?.legal_name || "Ihrer Hausverwaltung";
  const isSingleUnit = overview.units_total === 1;
  const properties = overview.properties || [];
  const myContactId = me?.contact?.id;

  return (
    <div className="space-y-8 sm:space-y-10" data-testid="owner-home">
      {/* =================== HERO – Premium Banking-Look =================== */}
      <section
        className="relative overflow-hidden rounded-3xl text-white"
        style={{
          background: `radial-gradient(circle at 100% 0%, ${NAVY_GLOW} 0%, ${NAVY_SOFT} 35%, ${NAVY} 100%)`,
          boxShadow: "0 24px 60px -20px rgba(15, 42, 77, 0.45)",
        }}
        data-testid="owner-hero"
      >
        {/* Dekorative Schatten/Blobs */}
        <div className="absolute -right-32 -top-32 w-[420px] h-[420px] rounded-full opacity-40"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,0.10), transparent 70%)" }} />
        <div className="absolute -left-16 -bottom-16 w-[280px] h-[280px] rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,0.08), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "linear-gradient(45deg, white 1px, transparent 1px), linear-gradient(-45deg, white 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

        <div className="relative z-10 p-6 sm:p-10 md:p-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-semibold text-white/90 border border-white/15">
            <ShieldCheck size={12} weight="fill" /> Eigentümerportal
          </div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-5 leading-[1.1]"
            data-testid="owner-greeting"
          >
            {formalGreeting(me?.contact)}
          </h1>
          <div className="mt-6 space-y-2 text-white/85 text-sm sm:text-base max-w-3xl">
            <p data-testid="hero-line-managed-by">
              {overview.properties_count === 1 ? "Ihre Immobilie wird" : "Ihre Immobilien werden"} durch die{" "}
              <span className="font-semibold text-white">{brand}</span> professionell betreut.
            </p>
            <p data-testid="hero-line-units">
              Aktuell {isSingleUnit ? "befindet" : "befinden"} sich{" "}
              <span className="font-semibold text-white tabular-nums">{overview.units_total}</span>{" "}
              {isSingleUnit ? "Einheit" : "Einheiten"} in der Verwaltung.
            </p>
            <p className="text-white/70">Alle relevanten Informationen und Unterlagen finden Sie zentral an einem Ort.</p>
          </div>
        </div>
      </section>

      {/* =================== KPI-CARDS (3 statt 4, ohne Dokumente) =================== */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
        <KpiCardPremium
          icon={CurrencyEur}
          label="Monatliche Belastung"
          value={eur(costs.total_monthly)}
          sub="Ihr Anteil, durchschnittlich pro Monat"
          tone="navy"
          testid="kpi-monthly"
        />
        <KpiCardPremium
          icon={Clock}
          label="Nächste Fälligkeit"
          value={costs.next_due ? fmtDate(costs.next_due.next_due_date) : "—"}
          sub={costs.next_due ? `${costs.next_due.name} · ${eur(costs.next_due.own_amount)}` : "Keine offenen Termine"}
          tone="default"
          testid="kpi-next-due"
        />
        <KpiCardPremium
          icon={Receipt}
          label="Im aktuellen Monat"
          value={eur(costs.current_month_total)}
          sub="Quartal- & Jahreszahlungen"
          tone="amber"
          trend={makeTrend(costs.current_month_total, costs.previous_month_total)}
          testid="kpi-current-month"
        />
      </section>

      {/* =================== MEINE OBJEKTE – Property-Grid =================== */}
      <section className="space-y-4">
        <SectionHeader
          title="Meine Objekte"
          subtitle={`Alle Immobilien, an denen Sie als Eigentümer beteiligt sind`}
          accessory={
            properties.length > 0 && (
              <span className="text-xs text-slate-500 tabular-nums">
                {properties.length} {properties.length === 1 ? "Objekt" : "Objekte"}
              </span>
            )
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5" data-testid="owner-properties-grid">
          {properties.length === 0 && (
            <div className="col-span-full bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500 shadow-sm">
              <Buildings size={40} className="mx-auto mb-2 text-slate-300" /> Keine Objekte zugeordnet.
            </div>
          )}
          {properties.map((p) => {
            const myShare = (p.ownership_shares || []).find((s) => s.contact_id === myContactId)?.share_percent
              || (p.owner_contact_id === myContactId ? 100 : 0);
            return (
              <Link
                key={p.id}
                to={`/eigentuemer/objekte/${p.id}`}
                className="group bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200"
                data-testid={`owner-property-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-mono">
                    {p.property_number || "—"}
                  </div>
                  {myShare > 0 && myShare < 100 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-semibold">
                      {myShare}% Anteil
                    </span>
                  )}
                </div>
                <div className="font-semibold tracking-tight mt-2 text-slate-900 text-lg leading-tight">{p.name}</div>
                <div className="text-sm text-slate-500 mt-1 truncate">
                  {p.address?.street} {p.address?.house_number}, {p.address?.city}
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Details ansehen</span>
                  <ArrowRight size={14} className="text-slate-400 group-hover:translate-x-0.5 group-hover:text-slate-700 transition-all" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* =================== KOSTEN-SNAPSHOT – Pro Immobilie mit Trend =================== */}
      <section className="space-y-4">
        <SectionHeader
          title="Kostenübersicht"
          subtitle="Monatliche Durchschnittsbelastung gruppiert nach Objekt"
          accessory={<Link to="/eigentuemer/kosten" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 font-medium">Alle Details <ArrowRight size={12} /></Link>}
        />
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          {costs.groups.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              <CurrencyEur size={40} className="mx-auto mb-3 text-slate-300" />
              Es wurden noch keine laufenden Kosten erfasst.
            </div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="owner-cost-groups">
              {costs.groups.map((g, idx) => {
                const pctOfTotal = costs.total_monthly > 0 ? (g.monthly_total / costs.total_monthly) * 100 : 0;
                return (
                  <div key={g.property_id} className="px-5 sm:px-7 py-5 flex items-center justify-between gap-4 hover:bg-slate-50/70 transition-colors" data-testid={`owner-cost-group-${g.property_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-mono">{g.property_number || "—"}</span>
                        {g.share_percent < 100 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 text-[10px] font-medium">
                            Anteil {g.share_percent}%
                          </span>
                        )}
                      </div>
                      <div className="font-semibold text-slate-900 truncate mt-1">{g.property_name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {g.property_address?.street} {g.property_address?.house_number}, {g.property_address?.city}
                      </div>
                      {/* Mini-Anteilsbalken */}
                      <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-md">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pctOfTotal}%`, background: NAVY }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-2xl sm:text-3xl font-semibold tabular-nums" style={{ color: NAVY }}>{eur(g.monthly_total)}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">pro Monat</div>
                      {g.next_due_date && (
                        <div className="text-[11px] text-slate-500 mt-1.5">Nächste: <span className="font-medium text-slate-700">{fmtDate(g.next_due_date)}</span></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* =================== LETZTE DOKUMENTE – Modern mit PDF-Badges + Neu-Markern =================== */}
      <section className="space-y-4">
        <SectionHeader
          title="Letzte Dokumente"
          subtitle="Schnellzugriff auf Ihre wichtigsten Unterlagen"
          accessory={<Link to="/eigentuemer/dokumente" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 font-medium">Alle Dokumente <ArrowRight size={12} /></Link>}
        />
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          {docs.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              <FileText size={40} className="mx-auto mb-3 text-slate-300" />
              Keine Dokumente verfügbar.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {docs.slice(0, 5).map((d) => {
                const ext = fileExt(d);
                const fresh = isNewDoc(d.created_at);
                return (
                  <li key={d.id} className="group flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-slate-50/70 transition-colors" data-testid={`home-doc-${d.id}`}>
                    {/* PDF/Type Badge */}
                    <div className={`shrink-0 w-12 h-14 rounded-md flex items-center justify-center text-[10px] font-bold tracking-wider ${
                      ext === "PDF" ? "bg-red-50 text-red-700 border border-red-200" :
                      ["JPG","JPEG","PNG","WEBP"].includes(ext) ? "bg-blue-50 text-blue-700 border border-blue-200" :
                      ["DOC","DOCX"].includes(ext) ? "bg-indigo-50 text-indigo-700 border border-indigo-200" :
                      "bg-slate-50 text-slate-700 border border-slate-200"
                    }`}>
                      {ext === "PDF" ? <FilePdf size={20} weight="fill" /> : (ext || <FileText size={20} />)}
                    </div>
                    {/* Title + Meta */}
                    <button
                      onClick={() => setPreviewDocId(d.id)}
                      className="text-left min-w-0 flex-1"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 text-sm truncate">{d.title}</span>
                        {fresh && (
                          <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-bold uppercase tracking-wider" data-testid={`home-doc-new-${d.id}`}>
                            <Sparkle size={9} weight="fill" /> Neu
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {d.category} · {fmtDate(d.created_at)}{d.size ? ` · ${(d.size / 1024).toFixed(0)} KB` : ""}
                      </div>
                    </button>
                    {/* Aktionen */}
                    <div className="shrink-0 flex items-center gap-1">
                      <button
                        onClick={() => setPreviewDocId(d.id)}
                        className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                        title="Vorschau"
                        data-testid={`home-doc-preview-${d.id}`}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => downloadDoc(d)}
                        className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                        title="Herunterladen"
                        data-testid={`home-doc-download-${d.id}`}
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* =================== Eigentümerreport Footer Action =================== */}
      <div className="flex justify-end">
        <button
          onClick={async () => {
            const r = await fetch(`${API}/owner-portal/report-pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
            const blob = await r.blob(); const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "Eigentuemerreport.pdf"; a.click(); URL.revokeObjectURL(url);
          }}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
          style={{ background: NAVY }}
          data-testid="owner-report-download"
        >
          <FileArrowDown size={16} /> Vollständigen Eigentümerreport als PDF
        </button>
      </div>

      {previewDocId && <DocumentPreviewDrawer docId={previewDocId} onClose={() => setPreviewDocId(null)} />}
    </div>
  );
}

// Helper: Download document via fetch (Bearer Auth + Blob)
async function downloadDoc(d) {
  try {
    const r = await fetch(`${API}/owner-portal/documents/${d.id}/preview?download=1`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) throw new Error("Download fehlgeschlagen");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = d.original_filename || d.title || "dokument";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert("Dokument konnte nicht heruntergeladen werden.");
  }
}

function makeTrend(current, previous) {
  if (previous === undefined || previous === null) return null;
  if (!previous && !current) return null;
  const diff = (current || 0) - (previous || 0);
  if (Math.abs(diff) < 0.005) return { dir: "flat", label: "wie Vormonat" };
  const pct = previous > 0 ? Math.round((diff / previous) * 100) : null;
  return {
    dir: diff > 0 ? "up" : "down",
    label: pct !== null ? `${diff > 0 ? "+" : ""}${pct}% vs Vormonat` : `${diff > 0 ? "+" : ""}${eur(diff)} vs Vormonat`,
  };
}

// ============================================================================
// OWNER COSTS – Tabelle aller Kostenpositionen mit Doc-Preview-Drawer
// ============================================================================
export function OwnerCosts() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [previewDocId, setPreviewDocId] = useState(null);

  useEffect(() => { api.get("/owner-portal/costs").then((r) => setData(r.data)); }, []);
  if (!data) return <SkeletonList />;

  const items = filter === "all" ? data.items : data.items.filter((i) => i.property_id === filter);

  return (
    <div className="space-y-6" data-testid="owner-costs-page">
      <PageHeader title="Kosten & Abschläge" subtitle="Alle laufenden Kosten Ihrer Objekte – Klick auf eine Position öffnet das zugehörige Dokument." />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        <KpiCardPremium icon={CurrencyEur} label="Monatliche Belastung gesamt" value={eur(data.total_monthly)} sub="Ihr Anteil, durchschnittlich pro Monat" tone="navy" testid="costs-kpi-monthly" />
        <KpiCardPremium icon={Clock} label="Nächste Fälligkeit" value={data.next_due ? fmtDate(data.next_due.next_due_date) : "—"} sub={data.next_due ? `${data.next_due.name} · ${eur(data.next_due.own_amount)}` : "—"} tone="default" testid="costs-kpi-next-due" />
        <KpiCardPremium icon={Receipt} label="Im aktuellen Monat" value={eur(data.current_month_total)} sub={`Vormonat: ${eur(data.previous_month_total || 0)}`} tone="amber" trend={makeTrend(data.current_month_total, data.previous_month_total)} testid="costs-kpi-current" />
      </section>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === "all" ? "text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            style={filter === "all" ? { background: NAVY } : {}} data-testid="cost-filter-all">
            Alle Objekte
          </button>
          {data.groups.map((g) => (
            <button key={g.property_id} onClick={() => setFilter(g.property_id)}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === g.property_id ? "text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              style={filter === g.property_id ? { background: NAVY } : {}}>
              {g.property_number || g.property_name}
            </button>
          ))}
        </div>
        {/* Mobile-Cards */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="cost-mobile-list">
          {items.length === 0 && (
            <div className="py-12 text-center text-sm text-slate-500">
              <CurrencyEur size={32} className="mx-auto mb-2 text-slate-300" />
              Keine Kostenpositionen.
            </div>
          )}
          {items.map((it) => {
            const clickable = !!it.linked_document_id;
            return (
              <button key={it.id} type="button" onClick={() => clickable && setPreviewDocId(it.linked_document_id)} disabled={!clickable}
                className={`w-full text-left p-4 ${clickable ? "active:bg-blue-50" : ""}`} data-testid={`cost-card-${it.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate text-sm">{it.name}</div>
                    <div className="text-[11px] text-slate-500">{it.category}{it.share_percent < 100 ? ` · ${it.share_percent}%` : ""}</div>
                    <div className="text-[11px] text-slate-500 truncate">{it.property_name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-semibold tabular-nums" style={{ color: NAVY }}>{it.interval === "once" ? "—" : eur(it.monthly_average)}</div>
                    <div className="text-[10px] text-slate-500">pro Monat</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">{intervalLabel(it.interval)} · {fmtDate(it.next_due_date)}</span>
                  <span className="text-slate-700 tabular-nums">{eur(it.own_amount)}</span>
                </div>
                {clickable && <div className="mt-1 text-[10px] text-blue-700 inline-flex items-center gap-1"><Eye size={11} /> Dokument anzeigen</div>}
              </button>
            );
          })}
        </div>
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <Th>Kostenart</Th>
                <Th>Immobilie</Th>
                <Th>Intervall</Th>
                <Th>Nächste Fälligkeit</Th>
                <Th className="text-right">Betrag</Th>
                <Th className="text-right">Pro Monat</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody data-testid="cost-table-body">
              {items.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-sm text-slate-500">
                  <CurrencyEur size={32} className="mx-auto mb-2 text-slate-300" />
                  Keine Kostenpositionen für die ausgewählte Auswahl.
                </td></tr>
              )}
              {items.map((it) => {
                const clickable = !!it.linked_document_id;
                return (
                  <tr key={it.id} className={`border-t border-slate-100 ${clickable ? "hover:bg-blue-50/40 cursor-pointer" : "hover:bg-slate-50"}`}
                    onClick={() => clickable && setPreviewDocId(it.linked_document_id)}
                    data-testid={`cost-row-${it.id}`}>
                    <Td>
                      <div className="font-medium text-slate-900">{it.name}</div>
                      <div className="text-[11px] text-slate-500">{it.category}{it.share_percent < 100 ? ` · Ihr Anteil ${it.share_percent}%` : ""}</div>
                    </Td>
                    <Td className="text-xs text-slate-600">
                      <div className="font-mono text-slate-500">{it.property_number}</div>
                      <div>{it.property_name}</div>
                    </Td>
                    <Td className="text-xs">{intervalLabel(it.interval)}</Td>
                    <Td className="text-xs tabular-nums">{fmtDate(it.next_due_date)}</Td>
                    <Td className="text-right tabular-nums">{eur(it.own_amount)}</Td>
                    <Td className="text-right tabular-nums font-semibold" style={{ color: NAVY }}>{it.interval === "once" ? "—" : eur(it.monthly_average)}</Td>
                    <Td className="text-right">
                      {clickable ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 group-hover:text-blue-900">
                          <Eye size={13} /> Dokument
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">kein Doc</span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-right text-xs font-semibold text-slate-600">Summe pro Monat (Ihr Anteil):</td>
                  <td className="px-3 py-3 text-right tabular-nums font-bold" style={{ color: NAVY }} data-testid="cost-monthly-total">
                    {eur(items.filter((i) => i.interval !== "once").reduce((s, i) => s + i.monthly_average, 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {previewDocId && <DocumentPreviewDrawer docId={previewDocId} onClose={() => setPreviewDocId(null)} />}
    </div>
  );
}

// ============================================================================
// OWNER CALENDAR – Zahlungstimeline 12 Monate
// ============================================================================
export function OwnerCalendar() {
  const [data, setData] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);
  useEffect(() => { api.get("/owner-portal/calendar?months=12").then((r) => setData(r.data)); }, []);
  if (!data) return <SkeletonList />;

  const maxTotal = Math.max(1, ...data.months.map((m) => m.total));

  return (
    <div className="space-y-6" data-testid="owner-calendar-page">
      <PageHeader title="Zahlungskalender" subtitle="Übersicht der Fälligkeiten der nächsten 12 Monate — Monate mit hoher Belastung sind hervorgehoben." />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {data.months.map((m) => {
          const heavy = m.total > maxTotal * 0.6;
          return (
            <div key={m.key} className={`bg-white border rounded-xl p-4 sm:p-5 shadow-sm transition-colors ${heavy ? "border-amber-300" : "border-slate-200"}`} data-testid={`cal-month-${m.key}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-semibold text-slate-900 text-sm sm:text-base">{MONTH_NAMES[m.month - 1].slice(0, 3)}.</div>
                <div className="text-xs text-slate-500 tabular-nums">{m.year}</div>
              </div>
              <div className={`text-lg sm:text-2xl font-semibold tabular-nums ${heavy ? "text-amber-700" : ""}`} style={!heavy ? { color: NAVY } : {}}>
                {eur(m.total)}
              </div>
              {heavy && <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-700 font-semibold flex items-center gap-1"><Warning size={11} /> Hohe Belastung</div>}
              {m.items.length > 0 ? (
                <ul className="mt-3 space-y-1.5 text-xs">
                  {m.items.slice(0, 6).map((it, idx) => (
                    <li key={idx} className="flex items-center justify-between gap-2 group">
                      <button onClick={() => it.linked_document_id && setPreviewDocId(it.linked_document_id)}
                        disabled={!it.linked_document_id}
                        className={`text-left flex-1 truncate ${it.linked_document_id ? "hover:text-blue-700 hover:underline" : "text-slate-700"}`}>
                        {it.name}
                      </button>
                      <span className="tabular-nums text-slate-700 shrink-0 font-medium">{eur(it.amount)}</span>
                    </li>
                  ))}
                  {m.items.length > 6 && <li className="text-[10px] text-slate-400">+{m.items.length - 6} weitere</li>}
                </ul>
              ) : (
                <div className="mt-3 text-xs text-slate-400 italic">keine Zahlungen geplant</div>
              )}
            </div>
          );
        })}
      </div>

      {previewDocId && <DocumentPreviewDrawer docId={previewDocId} onClose={() => setPreviewDocId(null)} />}
    </div>
  );
}

// ============================================================================
// OWNER CONTRACTS – Verwalterverträge mit verknüpften Dokumenten
// ============================================================================
export function OwnerContracts() {
  const [items, setItems] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);
  useEffect(() => { api.get("/owner-portal/management-contracts").then((r) => setItems(r.data)); }, []);
  if (!items) return <SkeletonList />;

  const typeLabel = (t) => ({
    miet: "Mietverwaltung", weg: "WEG-Verwaltung", sondereigentum: "Sondereigentumsverwaltung",
    gewerbe: "Gewerbeverwaltung", sonstige: "Sonstige",
  })[t] || t;

  return (
    <div className="space-y-6" data-testid="owner-contracts-page">
      <PageHeader title="Verwalterverträge" subtitle="Vertragsumfang und zugehörige Dokumente" />

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Briefcase size={48} className="mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-600">Aktuell ist Ihnen kein Verwaltervertrag zugeordnet.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <article key={c.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`owner-contract-${c.id}`}>
              <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3 flex-wrap" style={{ background: "linear-gradient(180deg, rgba(15,42,77,0.04), transparent)" }}>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: NAVY }}>{typeLabel(c.type)}</div>
                  <h3 className="font-semibold text-lg text-slate-900 mt-1">{c.title}</h3>
                  {c.contract_number && <div className="text-[11px] text-slate-500 font-mono">Vertragsnr. {c.contract_number}</div>}
                </div>
                {c.fee_amount && (
                  <div className="text-right">
                    <div className="text-2xl font-semibold tabular-nums" style={{ color: NAVY }}>{eur(c.fee_amount)}</div>
                    <div className="text-[11px] text-slate-500">{intervalLabel(c.fee_interval)}{c.fee_per_unit ? " · pro Einheit" : " · pauschal"}</div>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Laufzeit</div>
                  <div>{fmtDate(c.start_date)} – {c.end_date ? fmtDate(c.end_date) : "unbefristet"}</div>
                  {c.notice_period_months && <div className="text-xs text-slate-500 mt-0.5">Kündigungsfrist: {c.notice_period_months} Monate</div>}
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Umfang</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.scope_description || "—"}</div>
                </div>
              </div>
              {c.linked_documents?.length > 0 && (
                <div className="px-6 pb-4">
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Vertragsdokumente</div>
                  <div className="flex flex-wrap gap-2">
                    {c.linked_documents.map((d) => (
                      <button key={d.id} onClick={() => setPreviewDocId(d.id)}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-xs hover:border-slate-400 hover:bg-slate-50 transition-colors"
                        data-testid={`contract-doc-${d.id}`}>
                        <FileText size={14} className="text-slate-500" />
                        <span className="font-medium text-slate-900">{d.title}</span>
                        <Eye size={12} className="text-slate-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {previewDocId && <DocumentPreviewDrawer docId={previewDocId} onClose={() => setPreviewDocId(null)} />}
    </div>
  );
}

// ============================================================================
// OWNER PROPERTIES (überarbeitet, in Banking-Style)
// ============================================================================
export function OwnerProperties() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/owner-portal/overview").then((r) => setData(r.data));
    api.get("/owner-portal/me").then((r) => setMe(r.data));
  }, []);
  if (!data) return <SkeletonList />;
  return (
    <div className="space-y-6" data-testid="owner-properties">
      <PageHeader title="Meine Objekte" subtitle="Alle Immobilien, an denen Sie als Eigentümer beteiligt sind." />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(data.properties || []).map((p) => {
          const myShare = (p.ownership_shares || []).find((s) => s.contact_id === me?.contact?.id)?.share_percent || (p.owner_contact_id === me?.contact?.id ? 100 : 0);
          return (
            <Link key={p.id} to={`/eigentuemer/objekte/${p.id}`}
              className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-400 transition-colors shadow-sm">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-mono">{p.property_number}</div>
              <div className="font-semibold tracking-tight mt-1 text-slate-900 truncate">{p.name}</div>
              <div className="text-sm text-slate-600 truncate">{p.address?.street} {p.address?.house_number}, {p.address?.city}</div>
              {myShare < 100 && (
                <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-800 text-[11px]">
                  Ihr Anteil: {myShare}%
                </div>
              )}
            </Link>
          );
        })}
        {(data.properties || []).length === 0 && <div className="col-span-full bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500"><Buildings size={36} className="mx-auto mb-2 text-slate-300" /> Keine Objekte zugeordnet.</div>}
      </div>
    </div>
  );
}

export function OwnerPropertyDetail() {
  const id = window.location.pathname.split("/").pop();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/owner-portal/properties/${id}`).then((r) => setP(r.data)); }, [id]);
  if (!p) return <SkeletonList />;
  return (
    <div className="space-y-4" data-testid="owner-property-detail">
      <Link to="/eigentuemer/objekte" className="text-sm text-slate-600 hover:text-slate-900">← Zurück zu meinen Objekten</Link>
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-mono">{p.property_number}</div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 mt-1">{p.name}</h1>
        <p className="text-sm text-slate-600">{p.address?.street} {p.address?.house_number}, {p.address?.zip} {p.address?.city}</p>
      </div>
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-6 py-3 border-b border-slate-100 text-sm font-semibold">Einheiten ({(p.units || []).length})</div>
        <ul className="divide-y divide-slate-100">
          {(p.units || []).map((u) => (
            <li key={u.id} className="px-6 py-3 flex items-center justify-between text-sm">
              <div><div className="font-mono text-slate-900">{u.unit_number}</div><div className="text-xs text-slate-500">{u.location || "—"}</div></div>
              <span className="text-xs text-slate-500">{u.living_area ? `${u.living_area} m²` : ""}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.status === "rented" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{u.status === "rented" ? "vermietet" : "verfügbar"}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

// ============================================================================
// OWNER INCOME – unverändert, leichter Banking-Polish
// ============================================================================
export function OwnerIncome() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/owner-portal/income").then((r) => setData(r.data)); }, []);
  if (!data) return <SkeletonList />;
  const total = data.reduce((s, x) => s + x.amount, 0);
  const max = Math.max(1, ...data.map((x) => x.amount));
  return (
    <div className="space-y-6" data-testid="owner-income">
      <PageHeader title="Einnahmen" subtitle="Monatlicher Eingang über alle Mietverhältnisse" />
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="text-sm text-slate-500">Gesamteinnahmen</div>
        <div className="text-3xl font-semibold mt-1 tabular-nums" style={{ color: NAVY }}>{eur(total)}</div>
      </div>
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-6 py-3 border-b border-slate-100 text-sm font-semibold">Monatlicher Eingang</div>
        <div className="p-6 space-y-1.5">
          {data.length === 0 && <div className="py-8 text-center text-sm text-slate-500">Noch keine Einnahmen erfasst.</div>}
          {data.map((row) => (
            <div key={row.month} className="flex items-center gap-3 text-sm">
              <div className="w-16 tabular-nums text-slate-600 shrink-0">{row.month}</div>
              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(row.amount / max) * 100}%`, background: NAVY }} />
              </div>
              <div className="w-24 text-right tabular-nums font-medium shrink-0">{eur(row.amount)}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// OWNER DOCS – mit Vorschau-Drawer
// ============================================================================
export function OwnerDocs() {
  const [docs, setDocs] = useState([]);
  const [previewDocId, setPreviewDocId] = useState(null);
  useEffect(() => { api.get("/owner-portal/documents").then((r) => setDocs(r.data)); }, []);

  return (
    <div className="space-y-6" data-testid="owner-docs">
      <PageHeader title="Dokumente" subtitle="Bescheide, Rechnungen, Verträge und Korrespondenz" />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100">
        {docs.length === 0 && <div className="py-12 text-center text-sm text-slate-500"><FileText size={36} className="mx-auto mb-2 text-slate-300" /> Keine Dokumente verfügbar.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 px-6 py-3 hover:bg-slate-50" data-testid={`owner-doc-${d.id}`}>
            <button onClick={() => setPreviewDocId(d.id)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
              <FileText size={18} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium truncate">{d.title}</div>
                <div className="text-xs text-slate-500 truncate">{d.category} · {fmtDate(d.created_at)} · {(d.size / 1024).toFixed(0)} KB</div>
              </div>
            </button>
            <button onClick={() => setPreviewDocId(d.id)} className="text-slate-600 hover:text-slate-900 p-2 -mr-2" title="Vorschau" data-testid={`owner-preview-${d.id}`}>
              <Eye size={18} />
            </button>
          </div>
        ))}
      </div>
      {previewDocId && <DocumentPreviewDrawer docId={previewDocId} onClose={() => setPreviewDocId(null)} />}
    </div>
  );
}

// ============================================================================
// OWNER PROFILE – mit Anrede + Verwalterverträgen
// ============================================================================
export function OwnerProfile() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [contracts, setContracts] = useState([]);
  useEffect(() => {
    api.get("/owner-portal/me").then((r) => setMe(r.data));
    api.get("/owner-portal/management-contracts").then((r) => setContracts(r.data || []));
  }, []);

  const c = me?.contact;
  const fullName = c?.contact_type === "company" || c?.contact_type === "institution"
    ? c?.company_name
    : `${c?.salutation === "herr" ? "Herr " : c?.salutation === "frau" ? "Frau " : ""}${c?.first_name || ""} ${c?.last_name || ""}`.trim();

  return (
    <div className="space-y-6" data-testid="owner-profile">
      <PageHeader title="Mein Profil" subtitle="Persönliche Daten und vertragliche Verknüpfungen" />
      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-1">
        <Row label="Name" value={fullName || user?.name} />
        <Row label="E-Mail" value={c?.emails?.[0] || user?.email} />
        <Row label="Telefon" value={c?.phones?.[0]} />
        <Row label="Eigentümer-Nummer" value={c?.owner_number} mono />
        <Row label="Anschrift" value={c?.address ? `${c.address.street || ""} ${c.address.house_number || ""}, ${c.address.zip || ""} ${c.address.city || ""}`.trim() : ""} />
      </section>

      {contracts.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-900 text-base">Verbundene Verwalterverträge</h2>
            <p className="text-xs text-slate-500 mt-0.5">Ihre aktiven Verwalterverträge inkl. Dokumente</p>
          </div>
          <div className="px-6 py-4">
            <Link to="/eigentuemer/vertraege" className="inline-flex items-center gap-2 text-sm font-medium hover:underline" style={{ color: NAVY }}>
              <Briefcase size={16} /> {contracts.length} Vertrag/Verträge anzeigen <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// Shared UI helpers
// ============================================================================
function PageHeader({ title, subtitle }) {
  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "default" }) {
  const valueStyle = tone === "navy" ? { color: NAVY } : tone === "amber" ? { color: "#92400e" } : {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-[0.14em] font-semibold">
        <Icon size={14} /> <span className="truncate">{label}</span>
      </div>
      <div className="text-lg sm:text-2xl md:text-3xl font-semibold mt-1.5 sm:mt-2 tabular-nums break-words" style={valueStyle}>{value}</div>
      {sub && <div className="text-[10px] sm:text-[11px] text-slate-500 mt-1 sm:mt-1.5 line-clamp-2">{sub}</div>}
    </div>
  );
}

function KpiCardPremium({ icon: Icon, label, value, sub, tone = "default", trend = null, testid = "" }) {
  const accent = tone === "navy" ? NAVY : tone === "amber" ? "#B45309" : "#475569";
  const accentBg = tone === "navy" ? "rgba(15,42,77,0.07)" : tone === "amber" ? "rgba(180,83,9,0.08)" : "rgba(71,85,105,0.07)";
  return (
    <div
      className="group relative bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 hover:-translate-y-0.5"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold leading-snug">
          {label}
        </div>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105"
          style={{ background: accentBg, color: accent }}
        >
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div
        className="text-3xl sm:text-4xl font-semibold mt-4 tabular-nums tracking-tight leading-tight break-words"
        style={{ color: accent }}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] sm:text-xs text-slate-500 mt-2 line-clamp-2">{sub}</div>}
      {trend && (
        <div
          className={`mt-3 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            trend.dir === "up" ? "bg-rose-50 text-rose-700" :
            trend.dir === "down" ? "bg-emerald-50 text-emerald-700" :
            "bg-slate-100 text-slate-600"
          }`}
          data-testid={`${testid}-trend`}
        >
          {trend.dir === "up" ? <TrendUp size={10} weight="bold" /> :
           trend.dir === "down" ? <TrendDown size={10} weight="bold" /> : null}
          {trend.label}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle, accessory }) {
  return (
    <div className="flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {accessory}
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 ${className}`}>{children}</td>;
}

function intervalLabel(i) {
  return ({ monthly: "Monatlich", quarterly: "Quartalsweise", half_year: "Halbjährlich", yearly: "Jährlich", once: "Einmalig" })[i] || i;
}

function SkeletonDashboard() {
  return <div className="space-y-6 animate-pulse">
    <div className="h-40 bg-slate-100 rounded-2xl" />
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-28 bg-white border border-slate-200 rounded-xl" />)}</div>
    <div className="h-48 bg-white border border-slate-200 rounded-xl" />
  </div>;
}
function SkeletonList() {
  return <div className="space-y-3 animate-pulse">
    <div className="h-10 bg-slate-100 rounded w-1/3" />
    {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-white border border-slate-200 rounded-xl" />)}
  </div>;
}
